import { computed, ref, watch } from 'vue'
import createSteps from './partials/steps'
import { createForm } from './form'
import { deepMerge } from '../../helpers/json'

export function createMultistepForm(initialStages, initialValues = {}, submissionAction, submissionConfig = {}) {
    /**
     * The default form
     */
    const form = createForm(initialValues, submissionAction, submissionConfig)

    /**
     * The steps for the multistep form
     */
    const steps = createSteps(initialStages, form.validator, form.fields, form.errors)

    /**
     * Sets the progress for a multistep form
     * @param {Object} progress 
     * 
     * @returns {Boolean}
     */
    const setProgress = async (progress) => {
        let firstInvalidStep = null

        let step = progress.data.highest_step
        let stepsToIterate = [step]
        while (steps.getPreviousStageName(step) !== undefined) {
            stepsToIterate.unshift(steps.getPreviousStageName(step))

            step = steps.getPreviousStageName(step)
        }

        for (let stepIndex in stepsToIterate) {
            let stepData = steps.stage(stepsToIterate[stepIndex])

            if (stepData?.onReloadData instanceof Function) {
                await stepData.onReloadData(progress, steps)
            }

            if (firstInvalidStep !== null) {
                continue
            }

            if (!stepData?.isValid?.value) {
                firstInvalidStep = stepsToIterate[stepIndex]
                continue
            }

            //We should set the stage as activated if the step is valid and no previous stages are invalid
            steps.setStageActivated(stepsToIterate[stepIndex])
        }

        steps.current_stage.value = firstInvalidStep ?? progress.data.current_step
        steps.highest_stage.value = progress.data.highest_step

        //Progress was successful if nothing is invalid
        return firstInvalidStep === null
    }

    /**
     * An object containing a list of the data from all of the different stages
     */
    const data = computed(
        () => Object.values(steps.stages)
            .reduce(
                (data, stage) => deepMerge(data, stage.data.value),
                {}
            )
    )

    form.actions.removeDataWatch()
    watch(data, form.applyDataForSubmission, { deep: true, immediate: true })

    return {
        ...form,

        /**
         * In the default form this is just a ref of fields
         */
        data: data,

        /**
         * Handles setting the progress from the form if we want to reload it
         */
        setProgress: setProgress,

        /**
         * The steps we should have for the current form
         */
        steps: steps
    }
}